<template>
    <div id="config-themes">
        <FilterView placeholder="Chercher un thème..." v-model="searchTerm">
            <CustomButton
                v-if="selected.length"
                icon="trash-alt"
                class="default red-theme"
                @click="showConfirmMany = true"
            >
                Supprimer
            </CustomButton>
            <CustomButton icon="plus" class="default green-theme" @click="handleNew">
                Ajouter un thème
            </CustomButton>
        </FilterView>
        <Modal v-if="showEdit" v-model="theme" @dismiss="showEdit = false" @close="handleClose" />
        <Confirm v-if="showConfirmOne" @dismiss="showConfirmOne = false" @close="handleDeleteOne">
            <span slot="header">Supprimer le thème</span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer ce thème ?<br />
                Les groupes d'objets appartenant à ce thème ne seront pas supprimés.
            </span>
        </Confirm>
        <Confirm
            v-if="showConfirmMany"
            @dismiss="showConfirmMany = false"
            @close="handleDeleteMany"
        >
            <span slot="header">
                Supprimer
                {{
                    selected.length > 1
                        ? `les ${selected.length} thèmes sélectionnés`
                        : 'le thème sélectionné'
                }}
            </span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer tous les thèmes sélectionnés ?<br />
                Les groupes d'objets appartenant à ces thèmes ne seront pas supprimés.
            </span>
        </Confirm>
        <h2 class="title">Thèmes</h2>
        <LoadingDots v-if="isLoading" />
        <TablePager v-if="!isLoading">
            <ThemeHeader slot="header" :header="tableHeader" @select-all="handleSelectAll" />
            <ThemeRow
                v-for="item in filtered"
                :item="item"
                :key="item.id"
                :header="tableHeader"
                :handleEdit="handleEdit"
                :handleConfirm="handleConfirm"
                :selected="selected"
                @selected="handleSelect"
            />
        </TablePager>
    </div>
</template>

<script>
import FilterView from '@/components/config/Filter';
import Modal from '@/components/config/ModalTheme';
import Confirm from '@/components/config/DeleteConfirm';
import CustomButton from '@/components/IconButton';
import LoadingDots from '@/components/LoadingDots';
import ThemeHeader from '@/components/config/ThemeHeader';
import ThemeRow from '@/components/config/ThemeRow';
import TablePager from '@/components/TablePager';

export default {
    data() {
        return {
            themes: [],
            theme: null,
            selected: [],
            searchTerm: null,
            showConfirmOne: false,
            showConfirmMany: false,
            showEdit: false,
            isLoading: true,
            tableHeader: [
                { name: 'Thème' },
                { name: "Groupe d'objets" },
                { name: 'Icône' },
                { name: 'Couleur' },
            ],
        };
    },
    watch: {
        searchTerm() {
            this.selected.splice(0);
        },
    },
    computed: {
        filtered() {
            if (this.searchTerm) {
                const re = new RegExp(this.searchTerm, 'i');
                return this.themes.filter((t) => re.test(t.name));
            }
            return this.themes;
        },
    },
    mounted() {
        this.$http
            .get('/api/theme')
            .then(
                (response) => {
                    // success callback
                    this.themes = response.body;
                },
                (err) => {
                    // error callback
                    console.log(JSON.stringify(err));
                },
            )
            .finally(() => (this.isLoading = false));
    },
    methods: {
        _getTheme(id) {
            const theme = this.themes.find((t) => t.id === id);
            return {
                ...theme,
                objects: theme.objects.map((o) => ({
                    id: o.id,
                    text: o.name,
                })),
            };
        },
        handleSelect(event) {
            const { id, checked } = event;
            const index = this.selected.findIndex((s) => s === id);
            if (checked) {
                if (index === -1) {
                    this.selected.push(id);
                }
            } else {
                if (index > -1) {
                    this.selected.splice(index, 1);
                }
            }
        },
        handleSelectAll(event) {
            this.selected.splice(0);
            if (event) {
                console.log('select all');
            }
        },
        handleConfirm(item) {
            this.theme = this._getTheme(item);
            this.showConfirmOne = true;
        },
        handleEdit(item) {
            this.theme = this._getTheme(item);
            this.showEdit = true;
        },
        handleClose() {
            // Crée le nouveau thème, si le nom du thème est renseigné
            if (!this.theme.id && this.theme.name) {
                this.$http
                    .post('/api/theme', this.theme)
                    .then((response) => {
                        const theme = response.body;
                        this.themes.push(theme);
                        this.showEdit = false;
                        this.theme = null;
                    })
                    .catch();
                // Met à jour le thème, si le nom du thème est renseigné
            } else if (this.theme.id && this.theme.name) {
                this.$http
                    .put(`/api/theme/${this.theme.id}`, this.theme)
                    .then((response) => {
                        const theme = response.body;
                        const index = this.themes.findIndex((t) => t.id === theme.id);
                        this.themes.splice(index, 1, theme);
                        this.showEdit = false;
                        this.theme = null;
                    })
                    .catch();
            }
        },
        handleNew() {
            this.theme = {
                id: null,
                name: '',
                icon: '',
                color: '',
                objects: [],
            };
            this.showEdit = true;
        },
        handleDeleteOne() {
            this.$http
                .delete(`/api/theme/${this.theme.id}`)
                .then(() => {
                    const index = this.themes.findIndex((t) => t.id === this.theme.id);
                    this.themes.splice(index, 1);
                    this.showConfirmOne = false;
                })
                .catch();
        },
        handleDeleteMany() {
            const requests = this.selected.map((s) => this.$http.delete(`/api/theme/${s}`));
            Promise.all(requests)
                .then(() => {
                    this.showConfirmMany = false;
                    this.selected.splice(0);
                    this.isLoading = true;
                    return this.$http.get('/api/theme');
                })
                .then((response) => {
                    // success callback
                    this.themes = response.body;
                    this.isLoading = false;
                })
                .catch(() => {
                    this.isLoading = false;
                });
        },
    },
    components: {
        Modal,
        Confirm,
        FilterView,
        CustomButton,
        LoadingDots,
        ThemeHeader,
        ThemeRow,
        TablePager,
    },
};
</script>

<style lang="scss"></style>
